import { useState, useRef, useEffect, type ChangeEvent } from "react";
import moment from "moment-timezone";
import { useGetCertificateAsset } from "@/lib/api/birthdayFundCertificates";

const BASE_WIDTH = 1280;
const BASE_HEIGHT = 720;

interface PercentagePosition {
  x: number; // percentage 0-100
  y: number; // percentage 0-100
}

interface BulletItem {
  id: string;
  text: string;
}

const makeId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2, 10);
};

const DEFAULT_BULLETS: BulletItem[] = [
  { id: makeId(), text: "Priority access to Birthday Beacon perks" },
  { id: makeId(), text: "Seasonal celebration packages available" },
  { id: makeId(), text: "Dedicated concierge for your guests" }
];

const toPx = (percent: number, base: number) => (percent / 100) * base;

export interface CertificateDesignerData {
  certificateNumber: string;
  certificateNumberSize: number;
  certificateNumberPosition: PercentagePosition;
  
  locationName: string;
  locationNameSize: number;
  locationNamePosition: PercentagePosition;
  
  locationTagline: string;
  locationTaglineSize: number;
  locationTaglinePosition: PercentagePosition;
  
  infoBody: string;
  infoBodySize: number;
  infoBodyPosition: PercentagePosition;
  
  bullets: BulletItem[];
  bulletSize: number;
  bulletPosition: PercentagePosition;
  
  logoDataUrl: string | null;
  logoPosition: PercentagePosition;
  logoWidthPct: number;
  
  footerText: string;
  footerSize: number;
  footerPosition: PercentagePosition;

  notesExpiration: string;
  notesExpirationSize: number;
  notesExpirationPosition: PercentagePosition;
}

interface BirthdayFundCertificateDesignerProps {
  initialData?: Partial<CertificateDesignerData>;
  onSave?: (data: CertificateDesignerData) => void;
  onCreateInstance?: (data: CertificateDesignerData, previewElement: HTMLDivElement) => void;
  onExportPng?: (data: CertificateDesignerData, previewElement: HTMLDivElement) => void;
  onExportJpeg?: (data: CertificateDesignerData, previewElement: HTMLDivElement) => void;
  isProcessing?: boolean;
  isUpdate?: boolean;
  instanceCertificateId?: string;
  instanceExpiration?: string;
}

interface PositionControlsProps {
  label: string;
  position: PercentagePosition;
  onChange: (position: PercentagePosition) => void;
}

function PositionControls({ label, position, onChange }: PositionControlsProps) {
  return (
    <div className="space-y-2 rounded-xl border border-rose-100 bg-white/60 p-3 text-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-rose-500">{label} Position</p>
      <div className="grid grid-cols-2 gap-3">
        <label className="flex flex-col gap-1 text-[11px] font-semibold text-slate-600">
          X (%)
          <input
            type="range"
            min={0}
            max={100}
            value={position.x}
            onChange={(event) => onChange({ ...position, x: Number(event.target.value) })}
          />
          <span className="text-xs text-slate-500">{position.x.toFixed(0)}%</span>
        </label>
        <label className="flex flex-col gap-1 text-[11px] font-semibold text-slate-600">
          Y (%)
          <input
            type="range"
            min={0}
            max={100}
            value={position.y}
            onChange={(event) => onChange({ ...position, y: Number(event.target.value) })}
          />
          <span className="text-xs text-slate-500">{position.y.toFixed(0)}%</span>
        </label>
      </div>
    </div>
  );
}

interface SliderProps {
  label: string;
  min: number;
  max: number;
  step?: number;
  value: number;
  onChange: (value: number) => void;
  unit?: string;
  disabled?: boolean;
}

function Slider({ label, min, max, step = 1, value, onChange, unit = "px", disabled }: SliderProps) {
  const formattedValue = Number.isInteger(value) ? value.toString() : value.toFixed(2);
  return (
    <label className="flex flex-col gap-1 text-sm font-semibold text-slate-600">
      {label}
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        disabled={disabled}
      />
      <span className="text-xs text-slate-500">
        {formattedValue}{unit}
      </span>
    </label>
  );
}

export function BirthdayFundCertificateDesigner({
  initialData,
  onSave,
  onCreateInstance,
  onExportPng,
  onExportJpeg,
  instanceCertificateId,
  instanceExpiration,
  isProcessing = false,
  isUpdate = false,
}: BirthdayFundCertificateDesignerProps) {
  const certificateAssetResult = useGetCertificateAsset();
  const certificateAssetUrl = certificateAssetResult?.data?.data?.presigned_get_url;

  const [certificateNumber, setCertificateNumber] = useState(instanceCertificateId ?? initialData?.certificateNumber ?? onCreateInstance ? "#XXXXX" : "#001");
  const [certificateNumberSize, setCertificateNumberSize] = useState(initialData?.certificateNumberSize ?? 20);
  const [certificateNumberPosition, setCertificateNumberPosition] = useState<PercentagePosition>(
    initialData?.certificateNumberPosition ?? { x: 39.5, y: 22.5 }
  );

  const [notesExpiration, setNotesExpiration] = useState(instanceExpiration 
    ? `Expires in: ${instanceExpiration}` 
    : "Expires in: MMM DD YYYY"
  );
  const [notesExpirationSize, setNotesExpirationSize] = useState(initialData?.notesExpirationSize ?? 18);
  const [notesExpirationPosition, setNotesExpirationPosition] = useState<PercentagePosition>(
    initialData?.notesExpirationPosition ?? { x: 70, y: 4 }
  );

  const [locationName, setLocationName] = useState(initialData?.locationName ?? "Lighthouse Farms");
  const [locationNameSize, setLocationNameSize] = useState(initialData?.locationNameSize ?? 46);
  const [locationNamePosition, setLocationNamePosition] = useState<PercentagePosition>(
    initialData?.locationNamePosition ?? { x: 37.3, y: 30 }
  );

  const [locationTagline, setLocationTagline] = useState(initialData?.locationTagline ?? "Where celebrations meet the shoreline breeze");
  const [locationTaglineSize, setLocationTaglineSize] = useState(initialData?.locationTaglineSize ?? 24);
  const [locationTaglinePosition, setLocationTaglinePosition] = useState<PercentagePosition>(
    initialData?.locationTaglinePosition ?? { x: 37.3, y: 38 }
  );

  const [infoBody, setInfoBody] = useState(
    initialData?.infoBody ?? "Host premium milestone experiences with guided lighthouse tours, private tastings, and curated keepsakes for every guest."
  );
  const [infoBodySize, setInfoBodySize] = useState(initialData?.infoBodySize ?? 18);
  const [infoBodyPosition, setInfoBodyPosition] = useState<PercentagePosition>(
    initialData?.infoBodyPosition ?? { x: 37.3, y: 50 }
  );

  const [bullets, setBullets] = useState<BulletItem[]>(initialData?.bullets ?? DEFAULT_BULLETS);
  const [bulletSize, setBulletSize] = useState(initialData?.bulletSize ?? 18);
  const [bulletPosition, setBulletPosition] = useState<PercentagePosition>(
    initialData?.bulletPosition ?? { x: 38, y: 60 }
  );

  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(initialData?.logoDataUrl ?? null);
  const [logoPosition, setLogoPosition] = useState<PercentagePosition>(
    initialData?.logoPosition ?? { x: 70, y: 40 }
  );
  const [logoWidthPct, setLogoWidthPct] = useState(initialData?.logoWidthPct ?? 20);

  const [footerText, setFooterText] = useState(
    initialData?.footerText ?? "When calling to reserve, mention your Birthday Beacon certificate to unlock concierge planning & complimentary décor touches."
  );
  const [footerSize, setFooterSize] = useState(initialData?.footerSize ?? 16);
  const [footerPosition, setFooterPosition] = useState<PercentagePosition>(
    initialData?.footerPosition ?? { x: 8, y: 90 }
  );

  const [previewScale, setPreviewScale] = useState(() => {
    if (!!onCreateInstance) { return 1; }
    // Calculate smart initial scale based on screen size (CSS pixels, not physical pixels)
    if (typeof window === 'undefined') return 0.7;
    if (window.innerWidth >= 2200) { return 1.2; }
    if (window.innerWidth >= 1800) { return 1; }
    if (window.innerWidth >= 1400) { return 0.7; }
    
    const screenWidth = window.innerWidth; // Already in CSS pixels, devicePixelRatio handled by browser
    const availableWidth = screenWidth - 400 - 100; // subtract sidebar and padding
    const scaleByWidth = availableWidth / BASE_WIDTH;
    return Math.min(Math.max(scaleByWidth, 0.5), 1.2); // clamp between 0.6 and 1.2
  });

  useEffect(() => { if(instanceCertificateId) { setCertificateNumber(instanceCertificateId) } }, [instanceCertificateId]);
  useEffect(() => { if(instanceExpiration) { setNotesExpiration(`Expires in: ${moment(instanceExpiration).format("MMM DD YYYY")}`) } }, [instanceExpiration]);

  const previewRef = useRef<HTMLDivElement>(null);

  const getCurrentData = (): CertificateDesignerData => ({
    certificateNumber,
    certificateNumberSize,
    certificateNumberPosition,
    locationName,
    locationNameSize,
    locationNamePosition,
    locationTagline,
    locationTaglineSize,
    locationTaglinePosition,
    infoBody,
    infoBodySize,
    infoBodyPosition,
    bullets,
    bulletSize,
    bulletPosition,
    logoDataUrl,
    logoPosition,
    logoWidthPct,
    footerText,
    footerSize,
    footerPosition,
    notesExpiration,
    notesExpirationSize,
    notesExpirationPosition,
  });

  const handleSave = () => {
    if (onSave) {
      onSave(getCurrentData());
    }
  };

  const handleCreateInstance = () => {
    if (onCreateInstance && previewRef.current) {
      onCreateInstance(getCurrentData(), previewRef.current);
    }
  };

  const handleExportPng = () => {
    if (onExportPng && previewRef.current) {
      onExportPng(getCurrentData(), previewRef.current);
    }
  };

  const handleExportJpeg = () => {
    if (onExportJpeg && previewRef.current) {
      onExportJpeg(getCurrentData(), previewRef.current);
    }
  };

  const updateBullet = (id: string, next: string) => {
    setBullets(current => current.map(item => item.id === id ? { ...item, text: next } : item));
  };

  const removeBullet = (id: string) => {
    setBullets(current => current.filter(item => item.id !== id));
  };

  const addBullet = () => {
    setBullets(current => [...current, { id: makeId(), text: "" }]);
  };

  const handleLogoChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setLogoDataUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const resetLogo = () => setLogoDataUrl(null);

  const positionStyle = (position: PercentagePosition) => ({
    left: toPx(position.x, BASE_WIDTH),
    top: toPx(position.y, BASE_HEIGHT)
  });

  return (
    <div className="flex bg-gradient-to-br from-rose-50 via-amber-50 to-white text-slate-900 overflow-y-auto max-h-[95vh]">
      <div className="flex flex-col space-y-2 px-6 py-2">
        <div className="">
          <p className="font-bold uppercase">Birthday Fund Certificate Designer</p>
        </div>

        <div className="grid gap-4 lg:grid-cols-[minmax(360px,400px),1fr] max-h-[95%]">
          <div className="flex flex-col space-y-6 overflow-y-auto max-h-[100%]">
            <section className="rounded-2xl border border-rose-100 bg-white/70 p-5 shadow-sm backdrop-blur">
              <h2 className="text-lg font-semibold text-slate-900">Certificate Basics</h2>
              <p className="text-sm text-slate-500">Control the hero elements that always show on the template.</p>

              <div className="mt-4 space-y-4">
                <label className="flex flex-col gap-2 text-sm font-semibold text-slate-600">
                  Location / Property Name
                  <input
                    className="rounded-lg border border-rose-100 bg-white/70 px-3 py-2 text-base font-semibold"
                    value={locationName}
                    onChange={event => setLocationName(event.target.value)}
                  />
                </label>
                <Slider label="Location name size" min={28} max={72} value={locationNameSize} onChange={setLocationNameSize} />
                <PositionControls label="Location name" position={locationNamePosition} onChange={setLocationNamePosition} />

                <label className="flex flex-col gap-2 text-sm font-semibold text-slate-600">
                  Location Tagline
                  <input
                    className="rounded-lg border border-rose-100 bg-white/70 px-3 py-2 text-base"
                    value={locationTagline}
                    onChange={event => setLocationTagline(event.target.value)}
                  />
                </label>
                <Slider label="Tagline size" min={16} max={40} value={locationTaglineSize} onChange={setLocationTaglineSize} />
                <PositionControls label="Tagline" position={locationTaglinePosition} onChange={setLocationTaglinePosition} />
              </div>
            </section>

            <section className="rounded-2xl border border-amber-100 bg-white/70 p-5 shadow-sm backdrop-blur">
              <h2 className="text-lg font-semibold text-slate-900">Info & Highlights</h2>
              <div className="mt-4 space-y-4">
                <label className="flex flex-col gap-2 text-sm font-semibold text-slate-600">
                  Overview paragraph
                  <textarea
                    className="min-h-[90px] rounded-lg border border-amber-100 bg-white/70 px-3 py-2 text-base"
                    value={infoBody}
                    onChange={event => setInfoBody(event.target.value)}
                  />
                </label>
                <Slider label="Overview text size" min={14} max={28} value={infoBodySize} onChange={setInfoBodySize} />
                <PositionControls label="Overview block" position={infoBodyPosition} onChange={setInfoBodyPosition} />

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-600">Bullet points</p>
                    <button
                      type="button"
                      onClick={addBullet}
                      className="text-sm font-semibold text-rose-500 hover:text-rose-600"
                    >
                      + Add
                    </button>
                  </div>

                  <div className="space-y-3">
                    {bullets.map((bullet, index) => (
                      <div key={bullet.id} className="flex items-start gap-2">
                        <span className="mt-2 text-xs font-bold uppercase text-rose-400">{String(index + 1).padStart(2, "0")}</span>
                        <div className="flex-1">
                          <textarea
                            className="min-h-[60px] w-full rounded-lg border border-amber-100 bg-white/70 px-3 py-2 text-sm"
                            value={bullet.text}
                            onChange={event => updateBullet(bullet.id, event.target.value)}
                            placeholder="Add highlight"
                          />
                          {bullets.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeBullet(bullet.id)}
                              className="mt-1 text-xs font-semibold text-slate-400 hover:text-rose-500"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <Slider label="Bullet text size" min={14} max={28} value={bulletSize} onChange={setBulletSize} />
                <PositionControls label="Bullet block" position={bulletPosition} onChange={setBulletPosition} />
              </div>
            </section>

            <section className="rounded-2xl border border-rose-100 bg-white/70 p-5 shadow-sm backdrop-blur">
              <h2 className="text-lg font-semibold text-slate-900">Logo & Footer</h2>
              <div className="mt-4 space-y-4">
                <label className="flex flex-col gap-2 text-sm font-semibold text-slate-600">
                  Upload location logo
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml"
                    onChange={handleLogoChange}
                    className="text-xs"
                  />
                </label>
                {logoDataUrl && (
                  <div className="space-y-2 rounded-xl border border-rose-100 bg-white/80 p-3">
                    <img src={logoDataUrl} alt="Location logo preview" className="mx-auto max-h-24 object-contain" />
                    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                      <button
                        type="button"
                        onClick={resetLogo}
                        className="font-semibold text-rose-500 hover:text-rose-600"
                      >
                        Remove logo
                      </button>
                    </div>
                  </div>
                )}
                <Slider label="Logo width (% of canvas)" min={8} max={40} value={logoWidthPct} onChange={setLogoWidthPct} unit="%" />
                <PositionControls label="Logo" position={logoPosition} onChange={setLogoPosition} />

                <label className="flex flex-col gap-2 text-sm font-semibold text-slate-600">
                  Footer messaging
                  <textarea
                    className="min-h-[80px] rounded-lg border border-rose-100 bg-white/70 px-3 py-2 text-sm"
                    value={footerText}
                    onChange={event => setFooterText(event.target.value)}
                  />
                </label>
                <Slider label="Footer text size" min={12} max={26} value={footerSize} onChange={setFooterSize} />
                <PositionControls label="Footer" position={footerPosition} onChange={setFooterPosition} />
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white/70 p-5 shadow-sm backdrop-blur" >
              <h2 className="text-lg font-semibold text-slate-900">Preview scale</h2>
              <Slider label="Zoom" min={0.6} max={1.4} step={0.05} value={previewScale} onChange={setPreviewScale} unit="x" disabled={!!onCreateInstance} />
              <p className="text-xs text-slate-500">
                This only affects how the canvas is displayed in the browser so you can inspect details. The coordinate sliders above still use the native SVG ratios (1280 × 720).
              </p>
            </section>
          </div>

          <div className="rounded-3xl border border-white/60 bg-white/40 p-6 shadow-xl backdrop-blur">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.4em] text-rose-400">Preview</p>
                <h2 className="text-2xl font-serif text-slate-900">Live certificate canvas</h2>
              </div>
              <div className="flex items-center gap-3">
                <div className="rounded-full bg-rose-100/70 px-4 py-2 text-xs font-semibold text-rose-600">
                  {Math.round(previewScale * 100)}%
                </div>
              </div>
            </div>

            <div className="mb-4 flex flex-wrap gap-2">
              {onSave && (
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isProcessing}
                  className="rounded-lg bg-blue-500 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isProcessing ? (isUpdate ? "Updating..." : "Creating...") : (isUpdate ? "Update Template" : "Create Template")}
                </button>
              )}
              {onCreateInstance && (
                <button
                  type="button"
                  onClick={handleCreateInstance}
                  disabled={isProcessing}
                  className="rounded-lg bg-green-500 px-4 py-2 text-sm font-semibold text-white hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isProcessing ? "Generating..." : "Generate Certificate"}
                </button>
              )}
            </div>

            <div className="mt-6 overflow-auto rounded-2xl border border-slate-100 bg-slate-50/40 p-4 flex items-center justify-center min-h-0">
              <div
                className="relative rounded-xl shadow-2xl ring-1 ring-black/5"
                style={{
                  width: BASE_WIDTH * previewScale,
                  height: BASE_HEIGHT * previewScale,
                  maxWidth: '100%',
                  maxHeight: '100%',
                }}
              >
                <div
                  ref={previewRef}
                  className="absolute inset-0"
                  style={{
                    width: BASE_WIDTH,
                    height: BASE_HEIGHT,
                    transform: `scale(${previewScale})`,
                    transformOrigin: 'top left',
                  }}
                >
                  <img
                    src={certificateAssetUrl || '/assets/certificate.svg'}
                    alt="Birthday Beacon certificate"
                    className="absolute inset-0 h-full w-full rounded-xl object-cover"
                  />

                <div className="absolute inset-0 rounded-xl">
                  <div
                    className="absolute font-semibold uppercase tracking-[0.4em] text-white"
                    style={{
                      ...positionStyle(certificateNumberPosition),
                      fontSize: certificateNumberSize
                    }}
                  >
                    {certificateNumber}
                  </div>

                  <div
                    className="absolute text-center text-black-600 font-bold"
                    style={{
                      ...positionStyle(notesExpirationPosition),
                      fontSize: notesExpirationSize,
                      width: BASE_WIDTH * 0.4
                    }}
                  >
                    {notesExpiration}
                  </div>

                  <div
                    className="absolute text-left font-serif text-slate-900"
                    style={{
                      ...positionStyle(locationNamePosition),
                      fontSize: locationNameSize,
                      width: BASE_WIDTH * 0.5
                    }}
                  >
                    {locationName}
                  </div>

                  <div
                    className="absolute text-left font-medium text-rose-600"
                    style={{
                      ...positionStyle(locationTaglinePosition),
                      fontSize: locationTaglineSize,
                      width: BASE_WIDTH * 0.55
                    }}
                  >
                    {locationTagline}
                  </div>

                  <div
                    className="absolute text-left text-slate-600"
                    style={{
                      ...positionStyle(infoBodyPosition),
                      fontSize: infoBodySize,
                      width: BASE_WIDTH * 0.55,
                      lineHeight: 1.6
                    }}
                  >
                    {infoBody}
                  </div>

                  <div
                    className="absolute text-left text-slate-700"
                    style={{
                      ...positionStyle(bulletPosition),
                      fontSize: bulletSize,
                      width: BASE_WIDTH * 0.45
                    }}
                  >
                    <ul className="list-disc pl-8">
                      {bullets.filter(item => item.text.trim().length > 0).map(item => (
                        <li key={item.id} className="mb-1">
                          {item.text}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {logoDataUrl && (
                    <div
                      className="absolute"
                      style={{
                        ...positionStyle(logoPosition),
                        width: toPx(logoWidthPct, BASE_WIDTH)
                      }}
                    >
                      <img src={logoDataUrl} alt="Location logo" className="h-auto w-full object-contain" />
                    </div>
                  )}

                  <div
                    className="absolute text-left text-slate-700"
                    style={{
                      ...positionStyle(footerPosition),
                      textAlign: "center",
                      fontSize: footerSize,
                      width: BASE_WIDTH * 0.84,
                      lineHeight: 1.4
                    }}
                  >
                    {footerText}
                  </div>
                </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
